import React, { useCallback, useMemo, useState } from "react";
import { SendingDomainMethod } from "@common/types/domains/emailSending";
import { useSendingDomains } from "@api/sendingDomain.api";
import { Button } from "@design-system/Button/Button";
import { Section } from "@design-system/Section/Section";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { AddSendingDomainModal } from "./AddSendingDomainModal";
import { SendingDomainsView } from "./SendingDomainsView";
import { SendingEmailsSection } from "./SendingEmailsSection";
import { sendingDomainMethodLabel } from "./sendingDomainHelpers";
import {
    isSuggestedSendingDomainRow,
    SUGGESTED_SENDING_DOMAIN_ROW_ID,
    type SendingDomainRow,
} from "./sendingDomainsViewConfig";
import { useSuggestedCustomSender } from "./useSuggestedCustomSender";

export const SendingDomainsSettings: React.FC = () => {
    const { data: domains, isLoading } = useSendingDomains();
    const suggestion = useSuggestedCustomSender();
    const [search, setSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [addDomainInitial, setAddDomainInitial] = useState("");

    const openAddDomain = useCallback((domain = "") => {
        setAddDomainInitial(domain);
        setShowAddModal(true);
    }, []);

    const closeAddDomain = useCallback(() => {
        setShowAddModal(false);
        setAddDomainInitial("");
    }, []);

    const tableData = useMemo<SendingDomainRow[] | null>(() => {
        if (isLoading) {
            return null;
        }
        // Gmail is a per-sender OAuth connection, not a domain the workspace
        // owns or verifies (no DNS/SMTP setup, no shared "domain" concept across
        // senders) — it has its own `SendingDomainMethod.gmail` row purely so the
        // backend can key a sending email off a domain id, but it must never
        // surface in this "Custom sending domains" table. It still counts toward
        // the backend's per-owner domain limit and is still excluded from the
        // domain picker in `AddSendingEmailModal`.
        const rows: SendingDomainRow[] = (domains ?? []).filter(
            (domain) => domain.method !== SendingDomainMethod.gmail,
        );
        const suggestedDomain =
            suggestion?.kind === "dns" ? suggestion.domain : undefined;
        const hasSuggestedDomain =
            suggestedDomain !== undefined &&
            !rows.some((row) => row.domain.toLowerCase() === suggestedDomain);
        if (hasSuggestedDomain) {
            rows.unshift({
                id: SUGGESTED_SENDING_DOMAIN_ROW_ID,
                domain: suggestedDomain,
                isSuggested: true,
            });
        }
        if (!search) {
            return rows;
        }
        const normalizedSearch = search.toLowerCase();
        return rows.filter((domain) => {
            const searchable = [domain.domain];
            if (!isSuggestedSendingDomainRow(domain)) {
                searchable.push(sendingDomainMethodLabel(domain.method));
            }
            return searchable.some((value) =>
                value.toLowerCase().includes(normalizedSearch),
            );
        });
    }, [domains, isLoading, search, suggestion]);

    return (
        <section>
            <header className="settings__header">
                <h3>Sending domains</h3>
            </header>
            <main>
                <Section
                    heading="Custom sending domains"
                    subheading="Verify a domain with DNS records or your own SMTP server so Kick can send from addresses on that domain."
                >
                    <TablePageHeader search={search} onSearchChange={setSearch}>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() => openAddDomain()}
                        >
                            Add domain
                        </Button>
                    </TablePageHeader>
                    <SendingDomainsView
                        data={tableData}
                        onConnectDomain={openAddDomain}
                    />
                    <AddSendingDomainModal
                        show={showAddModal}
                        initialDomain={addDomainInitial}
                        onClose={closeAddDomain}
                    />
                </Section>
                <SendingEmailsSection />
            </main>
        </section>
    );
};
